import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, Truck, Wand2, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Nişan Hediyelik banner on venue detail page — modern editorial redesign.
 * Fetches 4 real featured products for the mosaic; dark warm palette with
 * shimmer accent, oversized italic headline and a bold pill CTA.
 */
const NisanHediyelikTeaserBanner = ({ venue, cityName: cityOverride, source = 'venue-detail-banner' }) => {
  const [tracked, setTracked] = useState(false);
  const [products, setProducts] = useState([]);
  const cityName = cityOverride || venue?.cityName;

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/api/shop/products?featured=true&limit=4`)
      .then(r => { if (alive) setProducts((r.data?.products || []).slice(0, 4)); })
      .catch(() => {
        // fallback: fetch any 4 products so the mosaic is never empty
        axios
          .get(`${API_URL}/api/shop/products?limit=4`)
          .then(r => { if (alive) setProducts((r.data?.products || []).slice(0, 4)); })
          .catch(() => {});
      });
    return () => { alive = false; };
  }, []);

  if (!cityName) return null;

  const handleClick = () => {
    if (tracked) return;
    setTracked(true);
    axios.post(`${API_URL}/api/interest/nisan-hediyelik-click`, {
      venueId: venue?.id,
      venueName: venue?.name,
      cityName,
      districtName: venue?.districtName,
      source,
    }).catch(() => { /* ignore */ });
  };

  const startingPrice = products.length
    ? Math.min(...products.map(p => Number(p.price) || 999))
    : 34;

  return (
    <Link
      to="/nisan-hediyelikleri"
      onClick={handleClick}
      className="group relative block w-full text-left rounded-[28px] overflow-hidden shadow-[0_20px_50px_-15px_rgba(190,24,93,0.35)] hover:shadow-[0_30px_70px_-15px_rgba(190,24,93,0.55)] transition-all duration-500"
      data-testid="nisan-hediyelik-teaser-banner"
    >
      {/* Deep base with warm rose→plum gradient */}
      <div className="relative bg-gradient-to-br from-[#1a0714] via-[#2d0a1f] to-[#4a0f2e] p-5 sm:p-6">
        {/* Glow orbs */}
        <div className="absolute -top-12 -right-8 w-48 h-48 rounded-full bg-gradient-to-br from-amber-400/40 to-rose-500/30 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-16 -left-8 w-56 h-56 rounded-full bg-gradient-to-br from-fuchsia-500/25 to-rose-600/25 blur-3xl pointer-events-none" aria-hidden="true" />

        {/* Diagonal shimmer sweep on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="absolute -inset-y-8 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-18deg] group-hover:translate-x-[400%] transition-transform duration-[1400ms] ease-out" />
        </div>

        <div className="relative grid grid-cols-[1fr_auto] gap-4 sm:gap-5 items-center">
          {/* LEFT — Copy */}
          <div className="min-w-0">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-300/15 backdrop-blur ring-1 ring-amber-200/25 px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.18em] text-amber-100">
              <Sparkles className="h-2.5 w-2.5" strokeWidth={2.6} />
              davetevibul.com butik
              <span className="ml-1 flex h-1.5 w-1.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
            </div>

            {/* Headline */}
            <h3 className="mt-2 font-display font-normal leading-[1.05] tracking-tight text-white text-[22px] sm:text-[26px]">
              Nişan hediyelikleri,{' '}
              <em className="italic font-normal bg-gradient-to-r from-amber-200 via-rose-200 to-amber-300 bg-clip-text text-transparent">
                {cityName}
              </em>
              <span className="block text-white/80 font-sans text-[12.5px] font-medium tracking-normal mt-1.5 leading-snug">
                Davet evine direkt teslim · isim &amp; tarih işleme ücretsiz
              </span>
            </h3>

            {/* Trust chips */}
            <div className="mt-3 flex items-center flex-wrap gap-1.5">
              {[
                { icon: Truck, t: 'Davet evine teslim' },
                { icon: Wand2, t: 'İsim işleme' },
                { icon: Package, t: '500₺ üzeri kargo ücretsiz' },
              ].map(({ icon: I, t }, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white/8 ring-1 ring-white/15 px-2 py-0.5 text-[10px] font-medium text-white/85 backdrop-blur">
                  <I className="h-2.5 w-2.5" strokeWidth={2.4} />
                  {t}
                </span>
              ))}
            </div>

            {/* Price + CTA row */}
            <div className="mt-4 flex items-center gap-3">
              <div className="min-w-0">
                <div className="text-[9.5px] uppercase tracking-[0.18em] text-white/50 font-bold leading-none">Başlangıç</div>
                <div className="mt-0.5 font-sans text-white font-extrabold text-[20px] tabular-nums leading-none">
                  ₺{startingPrice}
                  <span className="text-white/50 text-[11px] font-medium ml-1">&apos;den itibaren</span>
                </div>
              </div>
              <div className="ml-auto inline-flex items-center gap-2 rounded-full bg-white text-neutral-950 pl-4 pr-2 py-2 text-[12.5px] font-bold shadow-lg group-hover:bg-amber-300 group-hover:scale-[1.03] transition-all">
                Alışverişe Başla
                <span className="h-6 w-6 rounded-full bg-neutral-950 text-white flex items-center justify-center group-hover:bg-neutral-950/90 group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT — Product mosaic (2×2) */}
          <div className="hidden sm:grid grid-cols-2 gap-1.5 w-[140px] sm:w-[160px] shrink-0">
            {(products.length ? products : [null, null, null, null]).map((p, i) => (
              <div
                key={p?.id || i}
                className={`relative aspect-square rounded-xl overflow-hidden ring-1 ring-white/20 shadow-lg transition-transform duration-500 ${
                  i === 0 ? 'group-hover:-rotate-[3deg] group-hover:-translate-y-0.5' : ''
                } ${i === 1 ? 'group-hover:rotate-[3deg] group-hover:-translate-y-0.5' : ''} ${
                  i === 2 ? 'group-hover:rotate-[3deg] group-hover:translate-y-0.5' : ''
                } ${i === 3 ? 'group-hover:-rotate-[3deg] group-hover:translate-y-0.5' : ''}`}
              >
                {p ? (
                  <img src={p.images?.[0]} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-rose-900/40 to-amber-800/30" />
                )}
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-xl pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default NisanHediyelikTeaserBanner;
